import React, { useEffect } from "react";
import { reduxForm } from "redux-form";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import SmartRender from "containers/SmartRender";
import Spinner, { styles } from "components/Spinner";
import OrderLoginCard from "components/OrderLoginCard";
import Alert from "react-s-alert";
import isFunction from "lodash/isFunction";
import { createStructuredSelector } from "reselect";
import {
  fetchGuestOrder,
  fetchUserOrders,
  clearData,
  setData,
  fetchOrderDetails,
  fetchLiquidUrl,
  activateSimCard,
  fetchLoggedInUserDetails,
  fetchAgreementForm,
  emailAgreementForm,
  initiatePortIn,
  fetchEligibleServices,
  cancelPortOutRequest,
  fetchOutstandingBillsInfo,
  changeFormField
} from "ducks/actions";
import {
  makeSelectMinimumAge,
  makeSelectDateOptions,
  makeSelectMonthOptions,
  makeSelectYearOptions,
  makeSelectGuestOrder,
  makeSelectIsFetchingData,
  makeSelectManageOrderForm,
  makeSelectAppSettings,
  makeSelectThemeColors,
  makeSelectLocale,
  makeSelectSessionToken,
  makeSelectAuthToken,
  makeSelectUserOrders,
  makeSelectUserOrderRefs,
  makeSelectSimActivationPayload,
  makeSelectFormattedSelectedNumber,
  makeSelectOrderRef,
  makeSelectContractualAgreementForm,
  makeSelectWorkflow,
  makeSelectContractType,
  makeSelectReportLostSimForm,
  makeSelectLoggedInUserDetails,
  makeSelectEligibleActions,
  makeSelectEligiblePlans,
  makeSelectReversePortinStatus,
  makeSelectDeliveryDetailsItems,
  makeSelectPageValidations,
  makeSelectIsModalView,
  makeSelectModalType,
  makeSelectIsDeliveryInfoAvailable,
  makeSelectzEIdentifyUserData,
  makeSelectPageData,
  makeSelectServiceInstanceNumber,
  makeSelectCurrentPhoneNumber,
  makeSelectOutstandingBillAmount,
  makeSelectIsActiveTelcoUser,
  makeSelectIsOutstandingPayable
} from "ducks/selectors";
import {
  urlParam,
  canAllowAction,
  getActionUrl,
  gotoUrl,
  nextPageWithParams,
  getAuthorizationHeader,
  handleOnLoadAction,
  isEligibilityAPISupported,
  activateZendeskWidget,
  getDeepLink,
  loggedInUserDetailsAPIParams,
  activateZendeskMessagingWidget
} from "utils/helpers";
import {
  paths,
  forms,
  MOBILE_APP_BROWSER_PROTOCOL,
  CONTRACT_TYPES,
  ORDER_ACTION_TYPES
} from "utils/constants";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import { buttonThemes } from "ui-components/theme";
import { isWebview, setSessionToken } from "utils/localStorage";
import validate from "validations";
import { selectKoreUserData } from "ducks/selectors/koreCustomerData";

const ManageOrderHOC = Component =>
  function Comp(props) {
    const {
      formValues,
      isActionInProgress,
      fetchGuestOrder,
      fetchUserOrders,
      fetchEligibleActions,
      fetchLoggedInUserDetails,
      selectUserOrder,
      order,
      themeColors,
      locale,
      change,
      userToken,
      orderRef,
      contractualAgreementForm,
      zEIdentify,
      serviceInstanceNumber,
      currentPhoneNumber,
      fetchOutstandingBillsInfo,
      isActiveTelcoUser,
      appSettings,
      changeField
    } = props;
    const dispatch = useDispatch();
    const redirectAction = urlParam("redirect");
    const urlOrderRef = urlParam("order_ref");
    const startChatAfterLogin = urlParam("startChatAfterLogin");
    const buttonTheme = get(themeColors, "button") || buttonThemes.blue;
    const ott = urlParam("ott");
    const enableOTT = get(appSettings, "generic.manage_order_ott");
    const koreUserData = useSelector(selectKoreUserData);
    const prefilzEForm = get(
      appSettings,
      "layout.header.help.prefilZendeskForm",
      true
    );

    useEffect(() => {
      if (enableOTT && ott) {
        fetchGuestOrder({ ott }, order => {
          // Update formValues for supporting manage order refresh after ott login
          const orderRefOrPhoneNumber = get(order, "order_ref") || "";
          const sessionToken = get(order, "session_token") || "";
          changeField("orderRefOrPhoneNumber", orderRefOrPhoneNumber);
          changeField("sessionToken", sessionToken);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ott, enableOTT]);

    useEffect(() => {
      if (userToken) {
        // temp meassure to use eligibility API only for selected countries.

        isEligibilityAPISupported() && fetchEligibleActions();
        fetchUserOrders();
        fetchLoggedInUserDetails();
      } else {
        get(order, "orderRef") && fetchGuestOrder(formValues);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (urlOrderRef && urlOrderRef !== get(order, "orderRef", "")) {
        change("orderRefOrPhoneNumber", urlOrderRef);
        selectUserOrder(urlOrderRef);
      } else {
        const redirectAction = urlParam("redirect");
        const channel = get(order, "channel", "");
        const steps = get(order, "steps", []);
        const allowAction = canAllowAction(redirectAction, steps);
        const redirectUrl =
          allowAction && getActionUrl(redirectAction, channel);
        channel && redirectUrl && gotoUrl(redirectUrl);
      }

      if (order && startChatAfterLogin) {
        gotoUrl(paths.CHAT);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectAction, order, urlOrderRef]);

    /**
     * Outstanding bill handling
     */
    useEffect(() => {
      if (userToken && isActiveTelcoUser) {
        fetchOutstandingBillsInfo();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      handleOnLoadAction({
        ...props,
        downloadContractualAgreementForm
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadContractualAgreementForm = () => {
      const { token, timeStamp } = getAuthorizationHeader();
      const params = { order_ref: orderRef };
      const headers = {
        "p-token": token,
        "p-timestamp": timeStamp
      };
      dispatch(
        fetchAgreementForm(headers, params, data => {
          if (data) {
            setTimeout(() => {
              downloadContractualAgreement(data);
            }, 0);
          }
        })
      );
    };

    const emailContractualAgreement = () => {
      dispatch(
        emailAgreementForm((_, errors) => {
          if (isEmpty(errors))
            Alert.success(
              get(
                locale,
                "contractual_agreement_form_sent_to_email",
                "Your Contractual Agreement Form will be sent to your email for download"
              )
            );
        })
      );
    };

    const cancelPortOut = () => {
      const payload = {
        accountNumber: serviceInstanceNumber,
        msisdn: currentPhoneNumber,
        status: "CANCEL_REQUEST"
      };
      dispatch(
        cancelPortOutRequest(payload, (_, errors) => {
          if (isEmpty(errors)) {
            gotoUrl(paths.MANAGE_ORDER, true, {}, true);
          }
        })
      );
    };

    const reportLostSim = () => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: true, type: "reportLostSim" }
        })
      );
    };

    const showInitiatePortInModal = () => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: true, type: "initiatePortIn" }
        })
      );
    };

    const openLiveChat = () => {
      if (window.ENABLE_KORE_CHAT) {
        window.showKoreChat(koreUserData);
      } else {
        if (window.ENABLE_ZENDESK_CLASSIC) {
          if (prefilzEForm) {
            activateZendeskWidget(zEIdentify);
          } else {
            activateZendeskWidget();
          }
        } else {
          activateZendeskMessagingWidget();
        }
      }
    };

    const downloadContractualAgreement = (data = contractualAgreementForm) => {
      const downloadAnchor = document.createElement("a");
      const file = new Blob([data], {
        type: "application/pdf"
      });
      downloadAnchor.href = URL.createObjectURL(file); //`${CONTRACTUAL_AGREEMENT_FORM}`;
      downloadAnchor.download = "contractual_agreement.pdf";
      downloadAnchor.click();
    };

    if (isActionInProgress) return <Spinner size={200} sx={styles.fullPage} />;

    // ------ Rendering manage order page for countries without route authentication  ------
    /**
     * Manage Order page with open access
     * Customers need to enter their connection details to access other manager order features
     */
    if (isEmpty(order)) {
      return <OrderLoginCard {...props} />;
    }

    /**
     * Manage Order page with authorized access
     * If Customer has an order and logged in show detailed manage order page
     */
    return (
      <Component
        {...props}
        {...order}
        orderOrPhone={get(formValues, "orderRefOrPhoneNumber", "")}
        dob={get(formValues, "dob", "")}
        buttonTheme={buttonTheme}
        formValues={formValues}
        locale={locale}
        downloadContractualAgreementForm={downloadContractualAgreementForm}
        downloadContractualAgreement={downloadContractualAgreement}
        emailContractualAgreement={emailContractualAgreement}
        reportLostSim={reportLostSim}
        cancelPortOut={cancelPortOut}
        openLiveChat={openLiveChat}
        showInitiatePortInModal={showInitiatePortInModal}
      />
    );
  };

const navigateToOrderAction = (
  orderActionName,
  fullyQualifiedURL,
  anchorTarget
) => {
  let urlToUse = fullyQualifiedURL
    ? fullyQualifiedURL
    : `/manage/${orderActionName.replace(/_/g, "-")}`;

  //to check with c
  if (orderActionName === ORDER_ACTION_TYPES.OUTSTANDING_BILLS) {
    gotoUrl(paths.OUTSTANDING_BILLS);
    return;
  }

  if (anchorTarget === "_self") {
    gotoUrl(urlToUse);
  } else {
    window.open(urlToUse, anchorTarget);
  }
};

const mapStateToProps = createStructuredSelector({
  formValues: makeSelectManageOrderForm(),
  formValidations: makeSelectPageValidations(forms.MANAGE_ORDER_FORM),
  lostSimFormValues: makeSelectReportLostSimForm(),
  loggedInUserDetail: makeSelectLoggedInUserDetails(),
  isActionInProgress: makeSelectIsFetchingData([
    "guestOrder",
    "orderDetails",
    "userOrders",
    "emailAgreementForm",
    "cancelPortOut",
    "outstandingBillPayments"
  ]),
  order: makeSelectGuestOrder(),
  minimumAge: makeSelectMinimumAge(),
  locale: makeSelectLocale(),
  monthOptions: makeSelectMonthOptions(),
  dateOptions: makeSelectDateOptions(),
  yearOptions: makeSelectYearOptions(),
  appSettings: makeSelectAppSettings(),
  themeColors: makeSelectThemeColors(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  sessionToken: makeSelectSessionToken(),
  userToken: makeSelectAuthToken(),
  userOrders: makeSelectUserOrders(),
  userOrderRefs: makeSelectUserOrderRefs(),
  simActivationPayload: makeSelectSimActivationPayload(),
  orderRef: makeSelectOrderRef(),
  workflow: makeSelectWorkflow(),
  contractualAgreementForm: makeSelectContractualAgreementForm(),
  numberType: makeSelectContractType(),
  initialValues: () => ({
    orderRefOrPhoneNumber: urlParam("order_ref") || urlParam("phone_number")
  }),
  eligibileActions: makeSelectEligibleActions(),
  eligiblePlans: makeSelectEligiblePlans(),
  reversePortinEligibility: makeSelectReversePortinStatus(),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  isModalView: makeSelectIsModalView(),
  modalType: makeSelectModalType(),
  isDeliveryInfoAvailable: makeSelectIsDeliveryInfoAvailable(),
  dateOfBirthFieldOrder: makeSelectPageData("dateOfBirthFieldOrder"),
  zEIdentify: makeSelectzEIdentifyUserData(),
  serviceInstanceNumber: makeSelectServiceInstanceNumber(),
  currentPhoneNumber: makeSelectCurrentPhoneNumber(),
  outstandingBillAmount: makeSelectOutstandingBillAmount(),
  isActiveTelcoUser: makeSelectIsActiveTelcoUser(),
  hasOutstandingPayments: makeSelectIsOutstandingPayable()
});

export function mapDispatchToProps(dispatch) {
  return {
    fetchGuestOrder: (values, callback) => {
      const {
        nric,
        orderRefOrPhoneNumber,
        dob,
        sessionToken,
        userToken,
        ott
      } = values;
      const params = {
        nric,
        order_or_phone: orderRefOrPhoneNumber,
        dob: dob,
        activate: false,
        update_ttl: true,
        session_token: sessionToken,
        user_token: userToken,
        ott,
        token: sessionToken // sent for skipping redundant DOB validation when we already have a session_token
      };
      dispatch(
        fetchGuestOrder(params, order => {
          if (!isEmpty(order)) {
            isFunction(callback) && callback(order);
            gotoUrl(paths.MANAGE_ORDER, true, {
              reset: null,
              iccid: null,
              ott: null
            });
          }
          if (!isEmpty(order) && get(order, "session_token", false)) {
            setSessionToken(get(order, "session_token", ""));
          }
        })
      );
    },
    fetchLiquidUrl: (bodyParams, onSuccess) => {
      dispatch(fetchLiquidUrl(bodyParams, onSuccess));
    },
    fetchUserOrders: () => {
      dispatch(
        fetchUserOrders({}, orders => {
          if (!isEmpty(orders)) {
            gotoUrl(paths.MANAGE_ORDER, true, {
              reset: null,
              iccid: null,
              ott: null
            });
          }
          if (!isEmpty(orders) && get(orders, "session_token", false)) {
            setSessionToken(get(orders, "session_token", ""));
          }
        })
      );
    },
    manageAnotherOrder: () => {
      dispatch(
        clearData([
          forms.MANAGE_ORDER_FORM,
          "guestOrder",
          "verifyOTP",
          "orderDetails",
          "suspensionPaymentDetails",
          "eligibilitySettings"
        ])
      );
      if (window && window.koreLogout) {
        window.koreLogout();
      }
    },
    resetData: () => {
      dispatch(
        clearData(["verifyOTP", "orderDetails", "suspensionPaymentDetails"])
      );
    },
    setGuestOrder: order => {
      dispatch(setData({ key: "guestOrder", data: order }));
    },
    // setOrderAction: (action, setShowModal) => {
    //   setShowModal(true);
    //   dispatch(
    //     setData({ key: "guestOrder", data: { order_action_name: action } })
    //   );
    // },
    setOrderAction: selectedActionConfig => {
      dispatch(
        setData({
          key: "guestOrder",
          data: { order_action_name: selectedActionConfig.action }
        })
      );
      dispatch(
        setData({
          key: "guestOrder",
          data: { otp_action_type: selectedActionConfig.otpActionType }
        })
      );
    },
    activateSimCard: (params, callback) =>
      dispatch(activateSimCard(params, callback)),
    fetchLoggedInUserDetails: () => {
      const params = loggedInUserDetailsAPIParams();
      const newToken = urlParam("auth_token");
      const tokenHeader = newToken ? { "X-AUTH": newToken } : {};
      dispatch(fetchLoggedInUserDetails(tokenHeader, params));
    },
    fetchOrderDetailsAndNavigate: ({
      orderRef,
      orderActionName,
      sessionToken,
      closeModal,
      locale,
      fullyQualifiedURL,
      anchorTarget
    }) => {
      const params = {
        type: orderActionName,
        order_ref: orderRef,
        session_token: sessionToken
      };

      dispatch(
        fetchOrderDetails(params, (details, error) => {
          if (!isEmpty(details)) {
            isFunction(closeModal) && closeModal();

            navigateToOrderAction(
              orderActionName,
              fullyQualifiedURL,
              anchorTarget
            );
          } else {
            Alert.error(error);
          }
        })
      );
    },
    initiatePortIn: (params, callback) =>
      dispatch(initiatePortIn(params, callback)),
    fetchEligibleActions: () => {
      dispatch(fetchEligibleServices());
    },
    contactCustomerService: zEIdentify => {
      activateZendeskWidget(zEIdentify);
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: false, type: "reportLostSim" }
        })
      );
    },
    hideModal: () => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: false, type: "" }
        })
      );
    },
    fetchOutstandingBillsInfo: () => {
      dispatch(fetchOutstandingBillsInfo());
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.MANAGE_ORDER_FORM,
          field: field,
          value: value
        })
      );
    }
  };
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  nextPage: nextPageWithParams(
    paths.DASHBOARD,
    stateProps.selectedNumber,
    stateProps.userToken
  ),
  messageData: { authorizerName: get(stateProps, "order.authorizerName") },
  selectUserOrder: order_ref => {
    const userOrder = find(stateProps.userOrders, { order_ref });
    isEmpty(userOrder)
      ? dispatchProps.manageAnotherOrder()
      : dispatchProps.setGuestOrder(userOrder);
  },
  getLiquidUrl: () => {
    const deeplink = getDeepLink();
    const bodyParams = {
      order_id: stateProps.orderRef,
      redirect_url: isWebview()
        ? `${deeplink}${paths.WEBLOGIN}`
        : `${window.location.origin}${paths.USER_DETAILS}`
    };

    const onSuccess = (result, errors, status) => {
      const liquidUrl = get(result, "application_url");
      if (!errors && liquidUrl) {
        const finalUrl = isWebview()
          ? liquidUrl.replace("https", MOBILE_APP_BROWSER_PROTOCOL)
          : liquidUrl;

        gotoUrl(finalUrl);
      } else {
        if (status === 409) {
          gotoUrl(paths.USER_DETAILS);
        }
      }
    };

    dispatchProps.fetchLiquidUrl(bodyParams, onSuccess);
  },
  activateSimCardAndNavigate: navigate => {
    const isPortIn =
      get(stateProps, "order.contractType") === CONTRACT_TYPES.PORT_IN;

    const activateMethod = isPortIn
      ? dispatchProps.initiatePortIn
      : dispatchProps.activateSimCard;

    activateMethod(stateProps.simActivationPayload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  },
  onActionClicked: (
    params,
    action,
    otpActionType,
    anchorTarget,
    fullyQualifiedURL,
    requireOTPCall
  ) => {
    dispatchProps.setOrderAction({ action, otpActionType });
    if (requireOTPCall) {
      params.setShowModal(true);
    } else {
      navigateToOrderAction(action, fullyQualifiedURL, anchorTarget);
    }
  },
  contactCustomerService: () => {
    dispatchProps.contactCustomerService(stateProps.zEIdentify);
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);

const enhance = compose(
  withConnect,
  reduxForm({
    form: forms.MANAGE_ORDER_FORM,
    validate: validate,
    destroyOnUnmount: false
  })
);

export default enhance(ManageOrderHOC(SmartRender));
